import {
  CURRENT_USER_OPERATION,
  FORGOT_PASSWORD_OPERATION,
  LOGIN_OPERATION,
  RESET_PASSWORD_OPERATION,
  type GeneratedCurrentUserResponse,
  type GeneratedForgotPasswordRequest,
  type GeneratedLoginRequest,
  type GeneratedLoginResponse,
  type GeneratedResetPasswordRequest,
  type GeneratedResetPasswordResponse,
  UPDATE_CURRENT_USER_OPERATION,
  UPDATE_CURRENT_USER_PASSWORD_OPERATION,
  type GeneratedUpdateCurrentUserPasswordRequest,
  type GeneratedUpdateCurrentUserPasswordResponse,
  type GeneratedUpdateCurrentUserRequest,
  type GeneratedUpdateCurrentUserResponse,
} from './generated-auth';
import type { components } from './openapi-schema';
import { extractErrorMessage, readResponseBody, resolveUrl } from './http';
import { notifyUnauthorized } from './unauthorized-handler';

export type AuthValidationError = components['schemas']['ValidationError'];

interface AuthRequestErrorOptions {
  status?: number;
  validationError?: AuthValidationError | null;
}

export class AuthRequestError extends Error {
  status?: number;
  validationError: AuthValidationError | null;

  constructor(message: string, options: AuthRequestErrorOptions = {}) {
    super(message);
    this.name = 'AuthRequestError';
    this.status = options.status;
    this.validationError = options.validationError ?? null;
  }
}

function isValidationError(payload: unknown): payload is AuthValidationError {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const record = payload as Record<string, unknown>;
  return typeof record.field === 'string' && typeof record.errorType === 'string';
}

async function performAuthRequest<RequestBody, ResponseBody>(
  operation: {
    method: string;
    path: string;
  },
  options: {
    payload?: RequestBody;
    token?: string;
    networkErrorMessage: string;
    requestErrorMessage: string;
  }
) {
  let response: Response;

  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (options.payload !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (options.token) {
      headers.Authorization = `Bearer ${options.token}`;
    }

    response = await fetch(resolveUrl(operation.path), {
      method: operation.method.toUpperCase(),
      headers,
      ...(options.payload !== undefined
        ? { body: JSON.stringify(options.payload) }
        : {}),
    });
  } catch (error) {
    throw new AuthRequestError(
      error instanceof Error
        ? options.networkErrorMessage
        : options.networkErrorMessage.replace(
            '. Verifique se o backend está disponível.',
            '.'
          )
    );
  }

  const responsePayload = await readResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      notifyUnauthorized();
    }

    throw new AuthRequestError(
      extractErrorMessage(responsePayload, `${options.requestErrorMessage} (${response.status})`),
      {
        status: response.status,
        validationError: isValidationError(responsePayload) ? responsePayload : null,
      }
    );
  }

  return (responsePayload ?? null) as ResponseBody | null;
}

export async function loginRequest(
  credentials: GeneratedLoginRequest
): Promise<GeneratedLoginResponse> {
  const payload =
    await performAuthRequest<GeneratedLoginRequest, GeneratedLoginResponse>(
      LOGIN_OPERATION,
      {
        payload: credentials,
        networkErrorMessage:
          'Não foi possível ligar ao servidor. Verifique se o backend está disponível.',
        requestErrorMessage: 'Não foi possível entrar agora.',
      }
    );

  return (payload ?? {}) as GeneratedLoginResponse;
}

export async function currentUserRequest(
  token: string
): Promise<GeneratedCurrentUserResponse> {
  const payload = await performAuthRequest<never, GeneratedCurrentUserResponse>(
    CURRENT_USER_OPERATION,
    {
      token,
      networkErrorMessage:
        'Não foi possível obter os dados da sessão atual. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível obter o utilizador atual.',
    }
  );

  return (payload ?? {}) as GeneratedCurrentUserResponse;
}

export async function updateCurrentUserRequest(
  token: string,
  payload: GeneratedUpdateCurrentUserRequest
): Promise<GeneratedUpdateCurrentUserResponse> {
  const responsePayload = await performAuthRequest<
    GeneratedUpdateCurrentUserRequest,
    GeneratedUpdateCurrentUserResponse
  >(UPDATE_CURRENT_USER_OPERATION, {
    token,
    payload,
    networkErrorMessage:
      'Não foi possível atualizar os dados pessoais. Verifique se o backend está disponível.',
    requestErrorMessage: 'Não foi possível atualizar os dados pessoais agora.',
  });

  return (responsePayload ?? {}) as GeneratedUpdateCurrentUserResponse;
}

export async function updateCurrentUserPasswordRequest(
  token: string,
  payload: GeneratedUpdateCurrentUserPasswordRequest
): Promise<GeneratedUpdateCurrentUserPasswordResponse> {
  const responsePayload = await performAuthRequest<
    GeneratedUpdateCurrentUserPasswordRequest,
    GeneratedUpdateCurrentUserPasswordResponse
  >(UPDATE_CURRENT_USER_PASSWORD_OPERATION, {
    token,
    payload,
    networkErrorMessage:
      'Não foi possível atualizar a password. Verifique se o backend está disponível.',
    requestErrorMessage: 'Não foi possível atualizar a password agora.',
  });

  return (responsePayload ?? {}) as GeneratedUpdateCurrentUserPasswordResponse;
}

export async function forgotPasswordRequest(
  payload: GeneratedForgotPasswordRequest
): Promise<void> {
  await performAuthRequest<
    GeneratedForgotPasswordRequest,
    void
  >(FORGOT_PASSWORD_OPERATION, {
    payload,
    networkErrorMessage:
      'Não foi possível ligar ao servidor. Verifique se o backend está disponível.',
    requestErrorMessage: 'Não foi possível iniciar a recuperação de password agora.',
  });
}

export async function resetPasswordRequest(
  payload: GeneratedResetPasswordRequest
): Promise<GeneratedResetPasswordResponse> {
  const responsePayload = await performAuthRequest<
    GeneratedResetPasswordRequest,
    GeneratedResetPasswordResponse
  >(RESET_PASSWORD_OPERATION, {
    payload,
    networkErrorMessage:
      'Não foi possível ligar ao servidor. Verifique se o backend está disponível.',
    requestErrorMessage: 'Não foi possível redefinir a password agora.',
  });

  return (responsePayload ?? {}) as GeneratedResetPasswordResponse;
}
